import {Moment} from 'moment';
import {MomentAdapter} from '../../../../shared/utils/moment.utils';
import {PermissionScope} from '../../../account/modules/user/api/entity/userAccountSummary.model';
import {JwtTokenAccountModel} from './jwtTokenAccount.model';
import {JwtProviderModel} from './jwtProvider.model';
import {ValueAndLabelModel} from '../../../common/model/valueAndLabel.model';
import {Service} from '../../../provider/service/api/entity/providerServiceSummary.model';
import {JwtSubscriptionModel} from './jwtSubscription.model';

export class JwtTokenModel {
    token: string;
    expirationDate: Moment;
    account: JwtTokenAccountModel;
    businessId: string;
    emailAddress: string;
    name: string;
    admin: boolean;
    role: ValueAndLabelModel;
    scope: PermissionScope;
    superUser: boolean;
    providers: JwtProviderModel[] = [];
    additionalAccounts: JwtTokenAccountModel[] = [];
    subscriptions: JwtSubscriptionModel[] = [];
    permissionValues: string[] = [];

    static fromJson(jwtToken: string): JwtTokenModel {
        // console.log('read jwt token');
        const jwtTokenModel = new JwtTokenModel();
        jwtTokenModel.token = jwtToken;
        jwtTokenModel.readToken(jwtToken);

        // console.log(['Jwt token', jwtTokenModel]);

        return jwtTokenModel;
    }

    public isStillValid(): boolean {
        return this.expirationDate && MomentAdapter.current().get().isBefore(this.expirationDate);
    }

    private readToken(token: string): void {
        let payloadPart  = token.split('.')[1];
        if (!payloadPart) {
            throw new Error('JWT does not have a payload part');
        }
        payloadPart = payloadPart.split('-').join('+');
        payloadPart = payloadPart.split('_').join('/');
        // console.log(payloadPart);

        const base64 = atob(payloadPart);
        const utf8Decoder = new TextDecoder('utf-8');
        const payloadDecoded = JSON.parse(utf8Decoder.decode(new Uint8Array(base64.split('').map(c => c.charCodeAt(0)))));

        if (payloadDecoded.exp) {
            this.expirationDate = MomentAdapter.of(payloadDecoded.exp * 1000).get();
            // console.log(['expirationDate', this.expirationDate]);
        } else {
            throw new Error('JWT does not have an expiration date');
        }
        if (payloadDecoded.iss) {
            const issJson = JSON.parse(payloadDecoded.iss);
            const account = new JwtTokenAccountModel();
            account.name = issJson.account.name;
            account.businessCode = issJson.account.businessCode;
            account.theme = issJson.account.theme;
            this.account = account;
            this.businessId = issJson.businessId;
            this.emailAddress = issJson.emailAddress;
            this.name = issJson.name;
            this.scope = PermissionScope[issJson.scope];
            this.superUser = issJson.superUser;
            this.admin = issJson.admin;
            if (issJson.role) {
                this.role = new ValueAndLabelModel();
                this.role.value = issJson.role.value;
                this.role.label = issJson.role.label;
            }
            this.additionalAccounts = issJson.additionalAccounts.map(value => {
                const additionalAccount = new JwtTokenAccountModel();
                additionalAccount.name = value.name;
                additionalAccount.businessCode = value.businessCode;
                additionalAccount.theme = value.theme;
                return additionalAccount;
            });
            this.providers = issJson.providers.map(prov => {
                const provider = new JwtProviderModel();
                provider.name = prov.name;
                provider.accountBusinessCode = prov.accountBusinessCode;
                provider.providerBusinessCode = prov.providerBusinessCode;
                provider.services = prov.services.map(serv => {
                    const service = new ValueAndLabelModel();
                    service.value = serv.value;
                    service.label = serv.label;
                    return service;
                });
                return provider;
            });
            this.subscriptions = issJson.subscriptions.map(sub => {
               const subscription = new JwtSubscriptionModel();
               subscription.providerBusinessCode = sub.providerBusinessCode;
               subscription.service = new ValueAndLabelModel();
               subscription.service.value = sub.service.value;
               subscription.service.label = sub.service.label;
               subscription.isDefault = sub.isDefault;
               return subscription;
            });
        } else {
            throw new Error('JWT does not have an issuer');
        }
        // console.log(['jwtTokenDecoded', this]);
    }

    get currentProvider(): JwtProviderModel {
        // console.log(['currentProvider', this.account, this.providers]);
        if (!this.account) {
            return undefined;
        }
        return this.providers.find(value => value.accountBusinessCode === this.account.businessCode);
    }

    hasCurrentProviderService(service: Service): boolean {
        const currentProvider = this.currentProvider;
        if (!currentProvider) {
            return false;
        }
        return currentProvider.services.findIndex(value => value.value === service) > -1;
    }

    getSubscriptionByServiceValue(serviceValue: string) {
        return this.subscriptions.find(value => value.service.value === serviceValue);
    }

}
